/**
 * Wall-clock ↔ UTC conversion in an IANA timezone.
 *
 * Moved here from `run-scheduled-tasks/scheduleTime.ts` (#1721) so kin-chat can
 * reach it: an Edge Function cannot import from a sibling function's directory,
 * and `_shared/` is the only place both can see. `scheduleTime.ts` imports and
 * re-exports these, so every existing caller is unchanged.
 *
 * ⚠️ Behaviour is byte-identical on purpose. This decides when a scheduled task
 * actually fires, and the whole family of bugs it exists to prevent (#346,
 * #348, #353, #357, the Aug 3 2026 outage) was UTC-vs-local arithmetic. Do not
 * "simplify" the two-pass offset correction — one pass is wrong for about an
 * hour, twice a year, which is the kind of bug nobody can reproduce in March.
 *
 * ⚠️ `kin-inbox/firstRunAt.ts` still carries its OWN copy, pinned as agreeing
 * by `inboxFirstRunAt.test.ts`. Folding that one in too is a separate change;
 * it is out of scope here and the parity test still covers it.
 */

/**
 * How far `tz` is from UTC at a given instant, in ms.
 *
 * Derived from Intl rather than a fixed table so DST is handled by the
 * platform's tz database. Positive east of UTC.
 */
export function tzOffsetMs(instant: Date, tz: string): number | null {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hour12: false,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    }).formatToParts(instant).reduce<Record<string, string>>((acc, p) => {
      if (p.type !== "literal") acc[p.type] = p.value;
      return acc;
    }, {});
    const asUtc = Date.UTC(
      Number(parts.year), Number(parts.month) - 1, Number(parts.day),
      Number(parts.hour) % 24, Number(parts.minute), Number(parts.second),
    );
    if (Number.isNaN(asUtc)) return null;
    return asUtc - instant.getTime();
  } catch {
    return null; // unknown IANA zone
  }
}

/**
 * The UTC instant at which a given wall-clock time occurs in `tz`.
 *
 * Two passes: guess, measure the offset at the guess, correct, then re-measure
 * in case the correction crossed a DST boundary. One pass is wrong for roughly
 * one hour twice a year, which is exactly the kind of bug that reappears at
 * 2 AM in November and nobody can reproduce.
 */
export function zonedWallClockToUtc(
  year: number, month: number, day: number,
  hours: number, minutes: number, tz: string,
): Date | null {
  const guess = Date.UTC(year, month, day, hours, minutes, 0, 0);
  const off1 = tzOffsetMs(new Date(guess), tz);
  if (off1 === null) return null;
  const corrected = guess - off1;
  const off2 = tzOffsetMs(new Date(corrected), tz);
  if (off2 === null) return null;
  return new Date(guess - off2);
}
